'use client'

import { useCallback, useState, useEffect } from 'react'
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react'
import { useAppKit } from '@reown/appkit/react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'

interface TonWalletModalProps {
    open: boolean
    onClose: () => void
}

// Inline SVG icons for reliability
const TonkeeperIcon = () => (
    <svg viewBox="0 0 40 40" className="w-8 h-8">
        <defs>
            <linearGradient id="tonkeeper-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#45AEF5" />
                <stop offset="100%" stopColor="#0098EA" />
            </linearGradient>
        </defs>
        <rect width="40" height="40" rx="10" fill="url(#tonkeeper-grad)" />
        <path d="M12 14H28L20 28L12 14Z" fill="white" />
        <path d="M20 14L28 14L20 20V14Z" fill="white" fillOpacity="0.7" />
    </svg>
)

const WalletConnectIcon = () => (
    <svg viewBox="0 0 40 40" className="w-8 h-8">
        <circle cx="20" cy="20" r="20" fill="#3B99FC" />
        <path
            d="M13 18.5C16.5-3 23.5-3 27 18.5"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
            transform="translate(0, 3)"
        />
        <path
            d="M16 20C18-1.5 22-1.5 24 20"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
            transform="translate(0, 1)"
        />
    </svg>
)

const MyTonWalletIcon = () => (
    <svg viewBox="0 0 40 40" className="w-8 h-8">
        <rect width="40" height="40" rx="10" fill="#0098EA" />
        <circle cx="20" cy="16" r="6" fill="white" />
        <path d="M12 28C12 24 15.5 21 20 21C24.5 21 28 24 28 28" stroke="white" strokeWidth="2" fill="none" />
    </svg>
)

const MoreWalletsIcon = () => (
    <svg viewBox="0 0 40 40" className="w-8 h-8">
        <rect width="40" height="40" rx="10" fill="#2D3748" />
        <circle cx="20" cy="20" r="10" fill="#0098EA" />
        <path d="M15 20H25M20 15V25" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
)

// Official TON Wallet Chrome Extension icon
const TonWalletIcon = () => (
    <svg viewBox="0 0 40 40" className="w-8 h-8">
        <defs>
            <linearGradient id="ton-wallet-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0098EA" />
                <stop offset="100%" stopColor="#00B4D8" />
            </linearGradient>
        </defs>
        <rect width="40" height="40" rx="10" fill="url(#ton-wallet-grad)" />
        <path d="M12 16H28L20 28L12 16Z" fill="white" />
        <path d="M20 16V22" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
)

/**
 * Custom TON Wallet Modal - Compact version with Framer Motion
 * Provides quick access to popular TON wallets + WalletConnect via AppKit
 */
export function TonWalletModal({ open, onClose }: TonWalletModalProps) {
    const [tonConnectUI] = useTonConnectUI()
    const wallet = useTonWallet()
    const { open: openAppKit } = useAppKit()

    // Track which wallets have browser extensions detected
    const [detectedWallets, setDetectedWallets] = useState<Set<string>>(new Set())

    // Check for injected wallets on mount and when modal opens
    useEffect(() => {
        if (typeof window === 'undefined') return

        const detected = new Set<string>()

        // Check common TON wallet jsBridgeKeys
        // TON wallets inject their API with a tonconnect property (e.g., window.tonkeeper.tonconnect)
        // jsBridgeKey must match what TON Connect SDK expects (see wallet list in console)
        const bridgeKeys = [
            { key: 'tonkeeper', name: 'Tonkeeper', check: () => (window as any).tonkeeper?.tonconnect },
            { key: 'tonwallet', name: 'TON Wallet', check: () => (window as any).tonwallet?.tonconnect },
            { key: 'mytonwallet', name: 'MyTonWallet', check: () => (window as any).mytonwallet?.tonconnect },
            { key: 'openmask', name: 'OpenMask', check: () => (window as any).openmask?.tonconnect },
        ]

        bridgeKeys.forEach(({ key, name, check }) => {
            // Check both for the object existence AND the tonconnect property
            const hasWallet = (window as any)[key] !== undefined
            const hasTonConnect = check()

            if (hasWallet || hasTonConnect) {
                detected.add(name)
                console.log(`[TonModal] Detected installed: ${name} (window.${key}, tonconnect: ${!!hasTonConnect})`)
            }
        })

        setDetectedWallets(detected)
    }, [open])

    // Handle WalletConnect via Reown AppKit
    const handleWalletConnect = useCallback(async () => {
        try {
            onClose()
            await openAppKit()
        } catch (error) {
            console.error('[TonModal] WalletConnect/AppKit failed:', error)
        }
    }, [openAppKit, onClose])

    // Handle TON Connect modal (for all TON wallets)
    const handleTonConnect = useCallback(async () => {
        try {
            onClose()
            await tonConnectUI.openModal()
        } catch (error) {
            console.error('[TonModal] TON Connect failed:', error)
        }
    }, [tonConnectUI, onClose])

    // Handle specific wallet connection - with proper browser extension detection
    const handleSpecificWallet = useCallback(async (walletName: string) => {
        try {
            onClose()

            // IMPORTANT: Disconnect first if already connected to avoid "wallet already connected" error
            if (tonConnectUI.connected) {
                console.log('[TonModal] Disconnecting existing wallet before new connection...')
                try {
                    await tonConnectUI.disconnect()
                    // Small delay to ensure disconnect is processed
                    await new Promise(resolve => setTimeout(resolve, 100))
                } catch (disconnectError) {
                    console.warn('[TonModal] Disconnect failed (may not have been connected):', disconnectError)
                }
            }

            const wallets = await tonConnectUI.getWallets()

            // Debug: Log all available wallets with their properties
            console.log('[TonModal] Available wallets:', wallets.map(w => ({
                name: w.name,
                appName: w.appName,
                jsBridgeKey: 'jsBridgeKey' in w ? w.jsBridgeKey : undefined,
                injected: 'jsBridgeKey' in w && typeof window !== 'undefined' &&
                    (window as any)[w.jsBridgeKey as string] !== undefined
            })))

            // Find wallet by name with priority matching
            // Priority: 1. Exact match, 2. appName exact, 3. Name contains search, 4. AppName contains search
            const normalizedSearch = walletName.toLowerCase().replace(/\s+/g, '')

            // First try exact match on name or appName
            let targetWallet = wallets.find(w => {
                const normalizedName = w.name.toLowerCase().replace(/\s+/g, '')
                const normalizedAppName = w.appName.toLowerCase().replace(/\s+/g, '')
                return normalizedName === normalizedSearch || normalizedAppName === normalizedSearch
            })

            // If no exact match, try name/appName contains search
            if (!targetWallet) {
                targetWallet = wallets.find(w => {
                    const normalizedName = w.name.toLowerCase().replace(/\s+/g, '')
                    const normalizedAppName = w.appName.toLowerCase().replace(/\s+/g, '')
                    return normalizedName.includes(normalizedSearch) || normalizedAppName.includes(normalizedSearch)
                })
            }

            // Last resort: search contains name (but only if name is reasonably long to avoid "wallet" matching everything)
            if (!targetWallet) {
                targetWallet = wallets.find(w => {
                    const normalizedName = w.name.toLowerCase().replace(/\s+/g, '')
                    const normalizedAppName = w.appName.toLowerCase().replace(/\s+/g, '')
                    // Only match if the name is at least 6 chars to avoid generic matches
                    return (normalizedName.length >= 6 && normalizedSearch.includes(normalizedName)) ||
                        (normalizedAppName.length >= 6 && normalizedSearch.includes(normalizedAppName))
                })
            }

            if (targetWallet) {
                console.log('[TonModal] Found wallet:', targetWallet.name, targetWallet.appName)

                // Check if this is an injected (browser extension) wallet
                const jsBridgeKey = 'jsBridgeKey' in targetWallet ? targetWallet.jsBridgeKey : undefined
                const isInjected = jsBridgeKey && typeof window !== 'undefined' &&
                    (window as any)[jsBridgeKey] !== undefined

                console.log('[TonModal] Wallet details:', {
                    jsBridgeKey,
                    isInjected,
                    windowHas: jsBridgeKey ? !!(window as any)[jsBridgeKey] : false
                })

                // For ALL injected wallets (including TON Wallet), use direct jsBridge connection
                // This bypasses the openSingleWalletModal which fails with "unknown wallet" for wallets
                // not in the SDK's registry (like TON Wallet = "tonwallet")
                if (isInjected) {
                    console.log('[TonModal] Connecting to injected wallet via jsBridge:', jsBridgeKey)
                    try {
                        // Use the connector to connect with the injected wallet info
                        await tonConnectUI.connector.connect({
                            jsBridgeKey: jsBridgeKey as string
                        })
                        console.log('[TonModal] Successfully connected to injected wallet')
                        return
                    } catch (bridgeError: any) {
                        // Check for specific error types
                        const errorMessage = bridgeError?.message || String(bridgeError)

                        // If it's a manifest error, the extension validation is too strict
                        // Fall back to universal link if available
                        if (errorMessage.includes('manifest') || errorMessage.includes('Manifest')) {
                            console.warn('[TonModal] Manifest validation failed, trying universal link...')
                            // Try opening the wallet's universal link page
                            if ('universalLink' in targetWallet && targetWallet.universalLink) {
                                const tcLink = `${targetWallet.universalLink}/ton-connect?v=2&id=${Date.now()}`
                                window.open(tcLink, '_blank')
                                return
                            }
                        }

                        console.error('[TonModal] Injected wallet connection failed:', bridgeError)
                        // Fall through to modal approach
                    }
                }

                // For non-injected wallets or if injected connection failed, use modal
                // Note: openSingleWalletModal requires the wallet to be in TON Connect's registry
                console.log('[TonModal] Opening single wallet modal for:', targetWallet.appName)
                try {
                    await tonConnectUI.openSingleWalletModal(targetWallet.appName)
                } catch (modalError: any) {
                    console.warn('[TonModal] openSingleWalletModal failed:', modalError)
                    // If the wallet isn't in the registry, fall back to full modal
                    if (String(modalError).includes('unknown wallet')) {
                        console.log('[TonModal] Wallet not in registry, opening full modal')
                        await tonConnectUI.openModal()
                    } else {
                        throw modalError
                    }
                }
            } else {
                console.log('[TonModal] Wallet not found, opening full modal for:', walletName)
                await tonConnectUI.openModal()
            }
        } catch (error) {
            console.error(`[TonModal] ${walletName} connection failed:`, error)
            // Fallback to full modal
            try {
                await tonConnectUI.openModal()
            } catch (e) {
                console.error('[TonModal] Fallback modal also failed:', e)
            }
        }
    }, [tonConnectUI, onClose])

    const walletOptions = [
        {
            name: 'Tonkeeper',
            icon: <TonkeeperIcon />,
            description: 'Most popular TON wallet',
            onClick: () => handleSpecificWallet('tonkeeper'),
        },
        {
            name: 'TON Wallet',
            icon: <TonWalletIcon />,
            description: 'Official Chrome extension',
            onClick: () => handleSpecificWallet('ton wallet'),
        },
        {
            name: 'WalletConnect',
            icon: <WalletConnectIcon />,
            description: 'Mobile wallets',
            onClick: handleWalletConnect,
        },
        {
            name: 'MyTonWallet',
            icon: <MyTonWalletIcon />,
            description: 'Web extension',
            onClick: () => handleSpecificWallet('mytonwallet'),
        },
        {
            name: 'More Wallets',
            icon: <MoreWalletsIcon />,
            description: 'View all TON wallets',
            onClick: handleTonConnect,
        },
    ]

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                        onClick={onClose}
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{
                            type: "spring",
                            damping: 25,
                            stiffness: 350
                        }}
                        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[280px]"
                    >
                        <div className="bg-[#1a1a1a] rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                                <h3 className="text-white text-sm font-semibold">Connect TON</h3>
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={onClose}
                                    className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                                >
                                    <X className="h-4 w-4 text-neutral-400" />
                                </motion.button>
                            </div>

                            {/* Wallet Options */}
                            <div className="p-2 space-y-1">
                                {walletOptions.map((wallet, index) => (
                                    <motion.button
                                        key={wallet.name}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        onClick={wallet.onClick}
                                        whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.08)' }}
                                        whileTap={{ scale: 0.98 }}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 cursor-pointer transition-colors duration-150"
                                    >
                                        {/* Icon */}
                                        <div className="shrink-0">
                                            {wallet.icon}
                                        </div>

                                        {/* Text */}
                                        <div className="flex-1 text-left">
                                            <div className="flex items-center gap-2">
                                                <span className="text-white text-sm font-medium">{wallet.name}</span>
                                                {detectedWallets.has(wallet.name) && (
                                                    <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30">
                                                        Detected
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-neutral-500 text-[11px]">{wallet.description}</div>
                                        </div>

                                        {/* Arrow */}
                                        <svg
                                            className="w-4 h-4 text-neutral-500"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            viewBox="0 0 24 24"
                                        >
                                            <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </motion.button>
                                ))}
                            </div>

                            {/* Footer */}
                            <div className="px-4 py-2 border-t border-white/5">
                                <p className="text-neutral-600 text-[10px] text-center">
                                    Secured by TON Connect
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    )
}

